import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
import { ACHIEVEMENTS, RARITY_CONFIG } from '../../../components/progress/AchievementsSection';
import type { Achievement } from '../../../components/progress/AchievementsSection';
import { getAchievementIllustration, DjayIllustrationBase } from '../../../mascot';
import { DjaySpecimen } from '../../../mascot';
import { ACCESSORY_NAMES } from '../../../mascot/useDjayCustomization';
/**
 * LevelUpCardReveal — Renders the just-earned level achievement as a real
 * collectible CARD reveal inside practice-session feedback (App-Feel 2027 P3:
 * "Voortgang is Voelbaar"). Replaces the previous plain amber text line.
 *
 * Reaching a level that maps to a level-category achievement (3/5/7/10/12/15)
 * shows the achievement's collectible illustration, rarity framing, reward-look
 * microcopy, and — when the level grants a cosmetic — a preview of the unlocked
 * Djay reward look via <DjaySpecimen>.
 */
// Explicit level → achievement id map (level-category cards).
const LEVEL_ACHIEVEMENT_IDS: Record<number, string> = {
  3: 'level-3',
  5: 'level-5',
  7: 'level-7',
  10: 'level-10',
  12: 'level-12',
  15: 'level-15'
};
export function getLevelAchievement(level: number): Achievement | null {
  const id = LEVEL_ACHIEVEMENT_IDS[level];
  if (!id) return null;
  return ACHIEVEMENTS.find(a => a.id === id) ?? null;
}
interface LevelUpCardRevealProps {
  level: number;
  xp: number;
  title: string;
}
export function LevelUpCardReveal({
  level,
  xp,
  title
}: LevelUpCardRevealProps) {
  const achievement = getLevelAchievement(level);
  // Fallback: level has no collectible card → keep a lightweight celebratory line.
  if (!achievement) {
    return <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center" data-id="element-10918" data-mp="16wl79ycn9x20">
        <p className="text-amber-800 font-bold text-lg mb-1" data-id="element-10919" data-mp="2n6xak1ar0k6b">
          Gefeliciteerd met level {level}!
        </p>
        <p className="text-amber-700 font-medium text-sm" data-id="element-10920" data-mp="2ibrtpgw7dvp3">
          Je hebt nu {xp} XP. Blijf oefenen om nog verder te groeien.
        </p>
      </div>;
  }
  const rarity = RARITY_CONFIG[achievement.rarity];
  const Illustration = getAchievementIllustration(achievement.id);
  const grantsReward = achievement.accessory !== 'none' || !!achievement.djayVariantUnlocked;
  const rewardName = achievement.accessory !== 'none' ? ACCESSORY_NAMES[achievement.accessory] : achievement.djayVariantUnlocked ? `${achievement.djayVariantUnlocked}-look` : null;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className={`relative overflow-hidden rounded-2xl border-2 ${rarity.cardBorder} ${rarity.cardBg} ${rarity.glowColor} shadow-lg p-5`} data-id="element-10921" data-mp="2bje3q459fc4a">
      {/* Header: "kaart unlocked" */}
      <div className="flex items-center justify-center gap-2 mb-4" data-id="element-10922" data-mp="1sswazzkfggix">
        <SparklesIcon className={`w-4 h-4 ${rarity.textColor}`} aria-hidden="true" data-id="element-10923" data-mp="1la59k07y4khm" />
        <p className={`text-xs font-black uppercase tracking-wider ${rarity.textColor}`} data-id="element-10924" data-mp="1gd50rhjpp3mt">
          Nieuwe kaart verdiend
        </p>
        <SparklesIcon className={`w-4 h-4 ${rarity.textColor}`} aria-hidden="true" data-id="element-10925" data-mp="16epa1foljwdu" />
      </div>

      <div className="flex flex-col items-center text-center" data-id="element-10926" data-mp="12ddrtv81ydfo">
        {/* Collectible illustration reveal */}
        <motion.div initial={{
        scale: 0.6,
        rotate: -6,
        opacity: 0
      }} animate={{
        scale: 1,
        rotate: 0,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 220,
        damping: 16,
        delay: 0.1
      }} className="mb-3" data-id="element-10927" data-mp="xkawpinmmvxd">
          {Illustration ? <DjayIllustrationBase isUnlocked rarity={achievement.rarity} size="md" data-id="element-10928" data-mp="13dnq9bzgij5b">
              <Illustration isUnlocked size="md" data-id="element-10929" data-mp="18bpai08mcoid" />
            </DjayIllustrationBase> : <div className="w-24 h-24 flex items-center justify-center rounded-2xl bg-white/70 border border-white text-5xl" data-id="element-10930" data-mp="ukarvuimipbu">
              {achievement.emoji}
            </div>}
        </motion.div>

        {/* Card identity */}
        <h3 className="text-lg font-black text-slate-900" data-id="element-10931" data-mp="kp91ywgixj87">
          {achievement.name}
        </h3>
        <p className="text-sm font-medium text-slate-600 mt-0.5" data-id="element-10932" data-mp="pk69u022hmji">
          {achievement.description}
        </p>

        <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${rarity.badgeBg} ${rarity.badgeText}`} data-id="element-10933" data-mp="fo37ou77tnwk">
          {rarity.label}
        </div>

        {/* Reward-look preview (only when the level grants a cosmetic) */}
        {grantsReward && achievement.djayVariantUnlocked && <div className="mt-4 w-full flex flex-col items-center gap-2 pt-4 border-t border-white/60" data-id="element-10934" data-mp="3uky6gl26cjas">
            <p className="text-[11px] font-black uppercase tracking-wider text-slate-500" data-id="element-10935" data-mp="3qk4i5qktl2hd">
              Beloning-look
            </p>
            <DjaySpecimen look={{
          variant: achievement.djayVariantUnlocked,
          accessory: achievement.accessory
        }} size="md" emotion={achievement.djayEmotionUnlocked} animate={false} data-id="element-10936" data-mp="86euev0o9g7h" />
            {rewardName && <p className="text-xs font-bold text-slate-700" data-id="element-10937" data-mp="r3y31xj7wwtd">{rewardName}</p>}
          </div>}

        {grantsReward && !achievement.djayVariantUnlocked && rewardName && <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 border border-white" data-id="element-10938" data-mp="2su9i3bzigdnn">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-500" data-id="element-10939" data-mp="2nwrjaef7jetd">
              Beloning-look
            </span>
            <span className="text-xs font-bold text-slate-700" data-id="element-10940" data-mp="ruo2j257bj0j">
              {rewardName}
            </span>
          </div>}

        {/* XP footnote */}
        <p className="mt-4 text-xs font-medium text-slate-500" data-id="element-10941" data-mp="11py81zut68j2">
          Level {level} bereikt · {xp} XP
        </p>
      </div>
    </motion.div>;
}